import { cn } from "@lib/cn"
import { Loader2 } from "lucide-react"
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react"

type Variant = "primary" | "secondary" | "ghost" | "destructive" | "outline"
type Size = "sm" | "md" | "lg"

const variantClasses: Record<Variant, string> = {
  primary: "bg-accent text-ink-inverted hover:bg-accent-hover",
  secondary: "bg-inset text-ink hover:bg-border/60",
  ghost: "text-ink-muted hover:bg-inset hover:text-ink",
  destructive: "bg-danger text-ink-inverted hover:bg-danger-hover",
  outline: "border border-border text-ink-muted hover:border-border-strong hover:bg-inset",
}

/**
 * Touch first, density second (PRD-59). Every size clears the 44px WCAG
 * 2.5.5 target on a phone and tightens from `md` up, so a dense table row on
 * a laptop stays dense while the same button stays tappable on the bus. 17
 * call sites had hand-written `h-7`/`h-8` buttons to get that density; they
 * got an untappable control instead.
 */
const sizeClasses: Record<Size, string> = {
  sm: "min-h-11 gap-1.5 px-2.5 text-xs md:min-h-8",
  md: "min-h-11 gap-2 px-3.5 text-sm md:min-h-9",
  lg: "min-h-12 gap-2 px-5 text-base",
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "primary",
      size = "md",
      loading,
      disabled,
      leftIcon,
      rightIcon,
      children,
      ...props
    },
    ref,
  ) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "rounded-control inline-flex items-center justify-center font-medium transition-colors duration-150",
        "disabled:cursor-not-allowed disabled:opacity-60",
        variantClasses[variant],
        sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </button>
  ),
)
Button.displayName = "Button"
